import React, { Fragment, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useForm, Controller } from "react-hook-form"

import { getConditions } from '../api'
import { selectors as appSelectors } from '../features/app'
import { actionTypes as qaActionTypes, selectors } from '../features/qa'
import FieldSlider from '../components/FieldSlider'
import FieldRadioGroup from '../components/FieldRadioGroup'
import { AnalyticElement, AnalyticType, ApiResult, ICondition } from '../types.d'
import { IError, IErrorButton } from '../features/error/types'
import { actionTypes as errorActionTypes } from '../features/error'
import ErrorSummary from '../components/ErrorSummary'

interface ConditionValues {
  handleConditionSubmit: Function, 
  handleConditionsLoaded: Function, 
  handleAnalyticEvent: Function,
  handleHeadingChange: Function
}
const Condition: React.FC<ConditionValues> = ({ handleAnalyticEvent, handleConditionSubmit, handleConditionsLoaded, handleHeadingChange }) => {
  const dispatch = useDispatch();
  const disabled = useSelector(appSelectors.getAppIsLoading);
  const { handleSubmit, control, setValue, getValues, formState: { errors } } = useForm<any>();
  const [conditions, setConditions] = useState<ICondition[]>([]);
  const [conditionCategories, setConditionCategories] = useState<Array<string>>([]);
  const [fieldsetStep, setFieldsetStep] = useState<number>(0);
  const [hasRequestedConditions, setHasRequestedConditions] = useState<boolean>(false);

  const onFailureToLoadConditions = (msg: string) => {
    const btns: Array<IErrorButton> = [
      {
        title: 'Try again', 
        classes: 'button-solid', 
        callback: () => getCondition(), 
        clearErrorOnClick: true
      }
    ]
    const err: IError = { 
      icon: require('../assets/images/exclamation.svg'),
      title: 'Sorry, we seem to be experiencing some technical difficulties, please try again later.', 
      buttons: btns
    };
    dispatch({ type: errorActionTypes.SET_ERROR, payload: err });
  };
  const handleFieldFocus = (name: string) => {
    handleAnalyticEvent({
      type: AnalyticType.Focus, 
      element: AnalyticElement.Field,
      name
    });
  };
  const handleButtonClick = (name: string) => {
    handleAnalyticEvent({
      type: AnalyticType.Click, 
      element: AnalyticElement.Button,
      name
    });
  };
  const onSubmit = (data: any) => {
    dispatch({ type: qaActionTypes.SET_QA_CONDITIONS, payload: data });
    if (fieldsetStep+1 !== conditionCategories.length){
      handleHeadingChange(conditionCategories[fieldsetStep+1],fieldsetStep+1);
      setFieldsetStep(fieldsetStep+1);
    }
    else {
      handleConditionSubmit(data);
    }
  };
  const isYesNo = (v: ICondition): boolean => {
    return ((v.good.toUpperCase() === "NO" && v.poor.toUpperCase() === "YES") || (v.poor.toUpperCase() === "NO" && v.good.toUpperCase() === "YES"));
  };
  const setFormValues = async (r: any) => {
    for (let i = 0; i != r.length; ++i){
      const shouldSet = !isYesNo(r[i]);
      if (shouldSet){
        setValue(`condition-${r[i].number}`, 32, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
      }
    }
  };
  const getCondition = async () => {
    const traffic = await getConditions();
    if (traffic.result && traffic.result.length){
      const cats = traffic.result.reduce((acc: any, curr: any) => {
        !acc.includes(curr.section) && acc.push(curr.section);
        acc[curr.section]
        return acc; 
      }, []);
      setConditionCategories(cats);
      setConditions(traffic.result);
      setTimeout(() => setFormValues(traffic.result),100);
      
      cats.length && handleHeadingChange(cats[0], 0);
      handleConditionsLoaded();
    }
    else {
      onFailureToLoadConditions(traffic.errormessage);
    }
  };
  useEffect(() => {
    if (!hasRequestedConditions) {
      setHasRequestedConditions(true);
      getCondition();
    }
  });

  return (
    <Fragment>
      <form onSubmit={handleSubmit(onSubmit)} className="i-multi-fieldset">
        {conditionCategories.filter((o, i) => fieldsetStep === i).map((cat, i) => 
          <fieldset key={cat} disabled={disabled}>
            {/* <legend>{cat}</legend> */}
            {conditions.filter(c => c.section == cat).map((c, i) => 
              <Controller key={i} 
                control={control}
                name={`condition-${c.number}`}
                rules={{ required: `This question has not been answered: ${c.question}` }}  
                render={({ field: { onChange, value, name, ref } }) => {
                  const handleFieldChange = (v: number) => {
                    onChange(v);
                  };
                  const descPoor = c.poor == 'Poor' ? '' : c.poor;
                  const descAverage = c.average == 'Average' ? '' : c.average;
                  const descGood = c.good == 'Good' ? '' : c.good;
                  return ((c.good.toUpperCase() === "NO" && c.poor.toUpperCase() === "YES") || (c.poor.toUpperCase() === "NO" && c.good.toUpperCase() === "YES") ? 
                    <FieldRadioGroup
                      defaultValue={0}
                      name={name}
                      inputRef={ref}
                      label={c.question}
                      options={[{label: c.poor, value: 16}, {label: c.good, value: 48}]}
                      error={errors[`condition-${c.number}`]}
                      onChange={handleFieldChange}
                    />
                    :
                    <FieldSlider 
                      defaultValue={c.value}
                      // defaultSliderPosition={c.value}
                      min={16}
                      step={16}
                      max={48}
                      marks={[{value: 16, label: 'Poor'}, {value: 32, label: 'Average'}, {value: 48, label: 'Good'}]}
                      marksDescriptions={[{value: 16, label: descPoor}, {value: 32, label: descAverage}, {value: 48, label: descGood}]}
                      defaultMark={{value: 32, label: c.average}}
                      name={name} 
                      inputRef={ref}
                      label={`${c.question}`} 
                      error={errors[`condition-${c.number}`]} 
                      onChange={handleFieldChange}
                    />
                  )
                }} 
              />
            )}
            {/* <ErrorSummary errors={errors} /> */}
            <div className="i-button-group">
              <button type="submit" className="button-outline" onClick={() => handleButtonClick('Continue')}>Continue</button>
            </div>
          </fieldset>
        )}
      </form>
    </Fragment>
  )
}

export default Condition;

